import * as React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import { HelpCircle, Save } from "lucide-react";
import { FaRegCopy } from "react-icons/fa";
import { Checkbox } from "./ui/checkbox";
import { useClipboard } from "../../hooks/useClipboard";
import CreateRequestDialog from "./request-gallery/CreateRequestDialog";

interface RelatedQuestionsProps {
  questions: string[];
  onQuestionClick: (question: string) => void;
  requestType: "discuss" | "research";
}

interface QuestionActionItemProps {
  icon: React.ReactNode;
  tooltipContent: string;
  onClick: () => void;
}

const QuestionActionItem: React.FC<QuestionActionItemProps> = ({
  icon,
  tooltipContent,
  onClick,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center justify-center p-0 bg-transparent border-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-1 rounded"
          aria-label={tooltipContent}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

const QuestionCard: React.FC<{
  question: string;
  isSelected: boolean;
  onQuestionClick: (question: string) => void;
  onToggleSelection: (question: string) => void;
  onSave: (question: string) => void;
  onCopy: (question: string) => void;
}> = ({ question, isSelected, onQuestionClick, onToggleSelection, onSave, onCopy }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Send question: ${question}`}
        className="flex items-start gap-2 justify-between bg-primary-100 rounded-lg py-3 px-4 relative cursor-pointer hover:bg-primary-200 border border-transparent hover:border-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-1"
        onClick={() => onQuestionClick(question)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onQuestionClick(question);
          }
        }}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-normal text-left whitespace-normal text-primary-300">
            {question}
          </p>
        </div>
        <div className="flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelection(question)}
                aria-label="Select question"
                className="bg-neutral-white border-neutral-500"
              />
            </TooltipTrigger>
            <TooltipContent>Select question</TooltipContent>
          </Tooltip>
          <QuestionActionItem
            icon={<Save className="size-3.5" />}
            tooltipContent="Save to request library"
            onClick={() => onSave(question)}
          />
          <QuestionActionItem
            icon={<FaRegCopy className="size-3.5" />}
            tooltipContent="Copy question"
            onClick={() => onCopy(question)}
          />
        </div>
      </div>
    </TooltipTrigger>
    <TooltipContent>Click to send</TooltipContent>
  </Tooltip>
);

const QuestionsHeader: React.FC<{
  questions: string[];
  selectedQuestions: Set<string>;
  onCopyAll: () => void;
  onSaveSelected: () => void;
  onToggleSelectAll: () => void;
}> = ({ questions, selectedQuestions, onCopyAll, onSaveSelected, onToggleSelectAll }) => (
  <div className="flex items-center justify-between mb-3">
    <h4 className="flex items-center gap-2 text-base font-medium">
      Related Questions
      <HelpCircle className="size-3.5" />
    </h4>
    <div className="flex items-center gap-2">
      <QuestionActionItem
        icon={<FaRegCopy className="size-3.5" />}
        tooltipContent="Copy all questions"
        onClick={onCopyAll}
      />
      <QuestionActionItem
        icon={<Save className="size-3.5" />}
        tooltipContent="Save selected questions to request library"
        onClick={onSaveSelected}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Checkbox
            checked={selectedQuestions.size === questions.length && questions.length > 0}
            onCheckedChange={onToggleSelectAll}
            aria-label="Select all questions"
            className="bg-neutral-white border-neutral-500"
          />
        </TooltipTrigger>
        <TooltipContent>Select all questions</TooltipContent>
      </Tooltip>
    </div>
  </div>
);

function toggleSetItem(set: Set<string>, item: string): Set<string> {
  const newSet = new Set(set);
  if (newSet.has(item)) {
    newSet.delete(item);
  } else {
    newSet.add(item);
  }
  return newSet;
}

const RelatedQuestions: React.FC<RelatedQuestionsProps> = ({
  questions,
  onQuestionClick,
  requestType,
}) => {
  const [selectedQuestions, setSelectedQuestions] = React.useState<Set<string>>(new Set());
  const { copyToClipboard } = useClipboard();
  const [isCreateRequestOpen, setIsCreateRequestOpen] = React.useState(false);
  const [createRequestData, setCreateRequestData] = React.useState<
    { name: string; content: string } | undefined
  >(undefined);

  const handleSaveQuestion = (question: string) => {
    setCreateRequestData({ name: "", content: question });
    setIsCreateRequestOpen(true);
  };

  const handleSaveSelected = () => {
    if (selectedQuestions.size === 0) {
      toast.warning("No Selection", {
        description: "Please select at least one question",
      });
      return;
    }
    const combined = Array.from(selectedQuestions).join("\n\n");
    setCreateRequestData({ name: "", content: combined });
    setIsCreateRequestOpen(true);
  };

  const handleToggleSelectAll = () => {
    setSelectedQuestions(
      selectedQuestions.size === questions.length ? new Set() : new Set(questions)
    );
  };

  return (
    <TooltipProvider>
      <div>
        <QuestionsHeader
          questions={questions}
          selectedQuestions={selectedQuestions}
          onCopyAll={() =>
            copyToClipboard(
              questions.join("\n"),
              `All ${questions.length} questions copied to clipboard`
            )
          }
          onSaveSelected={handleSaveSelected}
          onToggleSelectAll={handleToggleSelectAll}
        />
        <div className="flex flex-col gap-y-2">
          {questions.map((question, qIndex) => (
            <QuestionCard
              key={qIndex}
              question={question}
              isSelected={selectedQuestions.has(question)}
              onQuestionClick={onQuestionClick}
              onToggleSelection={(q) => setSelectedQuestions(toggleSetItem(selectedQuestions, q))}
              onSave={handleSaveQuestion}
              onCopy={(q) => copyToClipboard(q, "Question copied to clipboard")}
            />
          ))}
        </div>
        <CreateRequestDialog
          open={isCreateRequestOpen}
          onOpenChange={setIsCreateRequestOpen}
          initialData={createRequestData}
          type={requestType}
        />
      </div>
    </TooltipProvider>
  );
};

export default RelatedQuestions;
